import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import React from 'react';
import Image from '../../../primitives/image';
import Container from '../../../primitives/grid/container';
import Col from '../../../primitives/grid/col';
import Row from '../../../primitives/grid/row';
import DotImage from '../../../../images/dot-pattern.svg';
import AccordionSet from '../accordion-set';
import RichTextEditorStyles from '../../../utils/rich-text-editor-styles';
import paddingDefault from '../../../utils/padding-default';
import { StyledButtonAsLink } from '../../../primitives/styled-button';
import { breakpoints } from '../../../primitives/tokens';
import { FaqsData } from '../types/faqs-types';
import { colors } from '../../../primitives/colors';
import { getSizesProp } from '../../../../sourcing/data/contentstack/asset-helpers';
import { CsAssetSize } from '../../../../sourcing/data/contentstack/content-item';

interface MediaContentImage {
    filename?: string;
    title?: string;
    description?: string;
    url?: string;
    contentStackAsset?: unknown;
}

interface MediaContentData {
    content?: string;
    disableBackgroundDots?: boolean;
    image?: MediaContentImage;
}

interface MediacontentFaqsProps {
    mediacontent: MediaContentData;
    faqs: FaqsData;
}

const styles = {
    richText: css({
        color: '#fff',
        'h2, h4, li': { color: '#fff' },
    }),
    richTextWrap: css({
        paddingRight: '20px',
        paddingLeft: '40px',
        paddingTop: '160px',
        paddingBottom: '160px',
        [breakpoints.tb]: {
            paddingLeft: '56px',
            paddingRight: '56px',
            paddingTop: '0',
            paddingBottom: '0',
        },
    }),
    buttonWrap: css({
        display: 'flex',
        marginTop: '48px',
        [breakpoints.mb]: { marginTop: '40px' },
    }),
    contentColInner: css({
        position: 'relative',
        marginBottom: '32px',
        height: '100%',
        paddingLeft: '8px',
        marginRight: '80px',
        [breakpoints.mb]: {
            marginBottom: '0',
            paddingLeft: '0',
            marginRight: '0',
        },
    }),
    dotPattern: css({
        display: 'block',
        position: 'absolute',
        top: '-48px',
        left: '-64px',
        width: '100%',
        height: '100%',
        [breakpoints.mb]: { display: 'none' },
    }),
    image: css({
        border: `1px solid ${colors.grey200}`,
        objectFit: 'scale-down',
        objectPosition: 'center',
        maxHeight: '100%',
        [breakpoints.mb]: { maxHeight: 'none' },
    }),
    imageWrapper: css({
        top: '0',
        position: 'absolute',
        width: '100%',
        maxWidth: 'none',
        height: '100%',
        [breakpoints.tb]: {
            display: 'none',
            maxWidth: '448px',
            position: 'relative',
        },
        [breakpoints.mb]: {
            position: 'relative',
            width: 'auto',
            height: 'auto',
        },
        img: css({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            objectFit: 'contain !important' as any,
            objectPosition: 'center',
        }),
    }),
    accordionColInner: css({
        paddingRight: '28px',
        [breakpoints.mb]: { paddingRight: 0 },
    }),
};

const MediacontentFaqs = ({ mediacontent, faqs }: MediacontentFaqsProps): JSX.Element => {
    const { link, heading, byline, questions } = faqs;
    const { content, image, disableBackgroundDots } = mediacontent;
    const hasImage = !!image?.url;

    const transformedData = {
        settings: faqs.settings,
        heading,
        byline,
        questions,
        link,
    };

    return (
        <Container>
            <div css={paddingDefault()}>
                <Row>
                    <Col breakpoints={{ dt: { span: 5 }, tb: { span: 12 }, mb: { span: 4 } }}>
                        <div css={styles.contentColInner}>
                            {content && (
                                <div css={styles.richTextWrap}>
                                    <div
                                        css={[RichTextEditorStyles, styles.richText]}
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{ __html: content }}
                                    />
                                </div>
                            )}

                            {hasImage && (
                                <div css={styles.imageWrapper}>
                                    <Image
                                        css={styles.image}
                                        image={image}
                                        alt={image.description}
                                        sizes={getSizesProp(CsAssetSize.HALF_MAX_WIDTH)}
                                    />
                                    {!disableBackgroundDots && <DotImage css={styles.dotPattern} />}
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col breakpoints={{ dt: { span: 7 }, tb: { span: 12 }, mb: { span: 4 } }}>
                        <div css={styles.accordionColInner}>
                            <AccordionSet faqs={transformedData} onMediaContent />
                            {link?.href && (
                                <div css={styles.buttonWrap}>
                                    <StyledButtonAsLink to={link.href}>{link.title}</StyledButtonAsLink>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>
        </Container>
    );
};

export const query = graphql`
  fragment MediacontentFaqsData on CsMediacontentFaqs {
    ... on CsMediacontentFaqs {
      mediacontent {
        content
        disableBackgroundDots
        image {
          filename
          title
          description
          url
          contentStackAsset(size: HALF_MAX_WIDTH)
        }
      }
      faqs {
        heading
        questions {
          questions {
            question
            answer
          }
        }
        link {
          href
          title
        }
      }
    }
  }
`;

export default MediacontentFaqs;
